import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { ACCESS_ENDPOINTS } from "@/store/api/endpoints";
import { DESIGNATION_ENDPOINTS } from "@/store/api/endpoints";
import { setAuthenticatedUser } from "./userSlice";
import { fetchDesignationOverview } from "@/store/designationOverviewSlice";
import { handleApiError } from "@/utils/apiErrorHandler";
import { clearError } from "./errorSlice";
import { physicianGroupConfirmationLinks } from "./profileSlice";
import axios from "axios";
import { ACCESS_LEVELS } from "./userAccessSlice";

const { DESIGNATION_PHASE } = DESIGNATION_ENDPOINTS;

// Define interface for query parameters
interface QueryParams {
  size?: number;
  page?: number;
  sort?: string | string[];
  status?: string;
  mpin?: number | string;
  [key: string]: any;
}

// Define the query type for fetchMyActiveDelegates
interface DelegateQueryParams {
  size?: number;
  page?: number;
  sort?: string;
  [key: string]: any;
}

// Helper function to build query string
const buildQueryString = (params: Record<string, unknown>): string => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          queryParams.append(key, item.toString());
        }
      });
    } else if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  });

  return queryParams.toString();
};

// Define thunks for access-related actions
export const validateUser = createAsyncThunk(
  "access/SECURE_VALIDATE",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.VALIDATE_USER);

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const userData = await response.json();

      // Also dispatch to the user slice to maintain both states in sync
      dispatch(setAuthenticatedUser(userData));

      return userData;
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to validate user");
    }
  }
);

export const updateAccessUser = createAsyncThunk(
  "access/SET_ACCESS_USER",
  async (user: AccessUser, { fulfillWithValue }) => {
    return fulfillWithValue(user);
  }
);

export const denyDelegate = createAsyncThunk(
  "access/DENY_DELEGATE",
  async (
    payload: {
      status: string;
      id: number;
      userId: number | null | undefined;
      userUsername: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.DENY_DELEGATE, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      // Transform the response to match the expected format used in the old app
      return formatDelegate(result);
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to deny delegate");
    }
  }
);
export const approveDelegate = createAsyncThunk(
  "access/APPROVE_DELEGATE",
  async (
    payload: {
      status: string;
      id: number;
      userId: number | null | undefined;
      userUsername: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.APPROVE_DELEGATE, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      // Transform the response to match the expected format used in the old app
      return formatDelegate(result);
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to approve delegate");
    }
  }
);
export const addDelegate = createAsyncThunk(
  "access/ADD_DELEGATE",
  async (
    payload: { status: string; userId: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.ADD_DELEGATE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      // Transform the response to match the expected format used in the old app
      return formatDelegate(result);
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to add delegate");
    }
  }
);

export const deleteDelegate = createAsyncThunk(
  "access/DELETE_DELEGATE",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(
        `${ACCESS_ENDPOINTS.DELETE_DELEGATE}/remove`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to delete delegate");
    }
  }
);

export const fetchMyPhysicians = createAsyncThunk(
  "access/FETCH_MY_PHYSICIANS",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const fixedQuery = { ...query };
      if (fixedQuery.username) {
        delete fixedQuery.username;
      }
      if (fixedQuery.sort && Array.isArray(fixedQuery.sort)) {
        fixedQuery.sort = fixedQuery.sort.map((sortObj) => {
          if (typeof sortObj === "object" && sortObj !== null) {
            const field = Object.keys(sortObj)[0];
            const direction = sortObj[field as keyof typeof sortObj];
            return `${field},${direction}`;
          }
          return sortObj;
        });
      }
      const queryString = buildQueryString(fixedQuery);
      const response = await fetch(
        `${ACCESS_ENDPOINTS.MY_PHYSICIANS}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch physicians");
    }
  }
);

export const fetchAllMyPhysicians = createAsyncThunk(
  "access/FETCH_ALL_MY_PHYSICIANS",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const fixedQuery = { ...query };
      if (fixedQuery.username) {
        delete fixedQuery.username;
      }
      if (fixedQuery.sort && Array.isArray(fixedQuery.sort)) {
        fixedQuery.sort = fixedQuery.sort.map((sortObj) => {
          if (typeof sortObj === "object" && sortObj !== null) {
            const field = Object.keys(sortObj)[0];
            const direction = sortObj[field as keyof typeof sortObj];
            return `${field},${direction}`;
          }
          return sortObj;
        });
      }
      const queryString = buildQueryString(fixedQuery);
      const response = await fetch(
        `${ACCESS_ENDPOINTS.MY_PHYSICIANS}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch physicians");
    }
  }
);

function formatFullName(
  first?: string,
  middle?: string | null,
  last?: string,
  lastNameFirst = false
): string {
  return lastNameFirst
    ? `${last || ""}${last ? "," : ""} ${first || ""} ${middle || ""}`
    : `${first || ""} ${middle || ""} ${last || ""}`;
}

function formatDelegate(delegate: any): Delegate {
  return {
    fullName: formatFullName(
      delegate.user?.firstName,
      delegate.user?.middleName,
      delegate.user?.lastName,
      true
    ),
    "user.email": delegate.user?.email || "",
    "user.firstName": delegate.user?.firstName || "",
    "user.id": delegate.user?.id,
    "user.username": delegate.user?.username || "",
    id: delegate.id,
    mpin: delegate.mpin,
    status:
      delegate.status?.charAt(0).toUpperCase() +
      delegate.status?.slice(1).toLowerCase() || "",
  };
}
export const fetchMyActiveDelegates = createAsyncThunk<
  { data: Delegate[]; totalItems: number }, // Return type
  DelegateQueryParams // Parameter type
>(
  "access/FETCH_MY_ACTIVE_DELEGATES",
  async (queryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      // Set default parameters if not provided
      /*const queryParams = {
       // size: 5,
      //  page: 0,
      //  sort: "user.firstName,asc",
        ...query
      };*/

      // Build the query string
      const queryString = buildQueryString(queryParams);

      // Use the correct URL format without the mpin in the path
      const response = await fetch(
        `${ACCESS_ENDPOINTS.FETCH_MY_ACTIVE_DELEGATES}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      // Transform the response to match the expected format used in the old app
      const formattedData = result.data.map((delegate: any) =>
        formatDelegate(delegate)
      );

      return {
        data: formattedData,
        totalItems: Number(result.totalItems) || 0,
        currentPage: Number(queryParams.page) || 0,
        previousQuery: queryParams,
      };
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch active delegates");
    }
  }
);

export const fetchMyDelegates = createAsyncThunk<
  { data: Delegate[]; totalItems: number }, // Return type
  DelegateQueryParams // Parameter type
>(
  "access/FETCH_MY_DELEGATES_BY_STATUS",
  async (queryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      // Set default parameters if not provided
      /*const queryParams = {
       // size: 5,
      //  page: 0,
      //  sort: "user.firstName,asc",
        ...query
      };*/

      // Build the query string
      const queryString = buildQueryString(queryParams);

      // Use the correct URL format without the mpin in the path
      const response = await fetch(
        `${ACCESS_ENDPOINTS.FETCH_MY_DELEGATE}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();

      // Transform the response to match the expected format used in the old app
      const formattedData = result.data.map((delegate: any) =>
        formatDelegate(delegate)
      );

      return {
        data: formattedData,
        totalItems: Number(result.totalItems) || 0,
        currentPage: Number(queryParams.page) || 0,
        previousQuery: queryParams,
      };
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch active delegates");
    }
  }
);

export const fetchDesignationPhase = createAsyncThunk(
  "access/fetchDesignationPhase",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(DESIGNATION_PHASE, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch designation phase");
    }
  }
);

export const fetchMyGroups = createAsyncThunk<
  { data: Group[]; totalItems: number }, // Return type
  QueryParams // Parameter type
>(
  "access/FETCH_MY_GROUPS",
  async (queryParams: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const queryString = buildQueryString(queryParams);

      const response = await fetch(
        `${ACCESS_ENDPOINTS.GROUPS_BY_USER}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();

      // Transform the response to match the expected format in the state
      // The API returns { rest, totalItems } but our state expects { data, totalItems }
      return responseData;
    } catch (error) {
      //  handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch groups");
    }
  }
);

export const searchPixeUsers = createAsyncThunk(
  "access/searchPixeUsers",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const queryString = buildQueryString(query);

      const response = await fetch(
        `${ACCESS_ENDPOINTS.SEARCH_PIXE_USERS}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to search PIXE users");
    }
  }
);

export const fetchPixeUserByMpin = createAsyncThunk(
  "access/FETCH_PIXE_USER_BY_MPIN",
  async (mpin: number | string, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await axios.get(
        `${ACCESS_ENDPOINTS.PIXE_USER_GET_BY_MPIN}?mpin=${mpin}`
      );
      const data =
        Object.keys(response.data?.data).length > 0 ? response.data.data : null;
      const id = data?.id || null;
      return {
        mpin,
        id: id || null,
        isRegistered: !!id,
        accessLevel: ACCESS_LEVELS.NO_ACCESS, // Using the default from your state
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch PIXE user by MPIN");
    }
  }
);

export const searchPhysicians = createAsyncThunk(
  "access/searchPhysicians",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const queryString = buildQueryString(query);

      const response = await fetch(
        `${ACCESS_ENDPOINTS.SEARCH_PHYSICIANS}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to search physicians");
    }
  }
);
//SECURE_PIXE_USER
export const updateUserPreferences = createAsyncThunk(
  "access/UPDATE_USER_PREFERENCES",
  async (user: AccessUser, { rejectWithValue, dispatch }) => {
    try {
      const response = await fetch(ACCESS_ENDPOINTS.SECURE_PIXE_USER, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }
      const responseData = await response.json();

      // Dispatch the setAuthenticatedUser action to update the user state
      return responseData;
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to update user preferences");
    }
  }
);

//REGISTER_PHYSICIAN
export const registerAsPhysician = createAsyncThunk(
  "access/REGISTER_PHYSICIAN",
  async (
    payload: { pin: string; tin: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      console.log("Payload in registerAsPhysician:", JSON.stringify(payload)); // Debugging line
      console.log("endpoint", ACCESS_ENDPOINTS.REGISTER_PHYSICIAN);
      const response = await fetch(ACCESS_ENDPOINTS.REGISTER_PHYSICIAN, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.error ||
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      const responseData = await response.json();

      return responseData;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      handleApiError(error as any, "access", dispatch);
      return rejectWithValue("Failed to validate group");
    }
  }
);

export const requestPhysician = createAsyncThunk(
  "access/REQUEST_PHYSICIAN",
  async (mpin: number | string, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const payload = { mpin };

      const response = await fetch(ACCESS_ENDPOINTS.MY_PHYSICIANS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to request physician");
    }
  }
);

export const deletePhysicianRequest = createAsyncThunk(
  "access/DELETE_PHYSICIAN_REQUEST",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(
        ACCESS_ENDPOINTS.DELETE_PHYSICIAN_REQUEST,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to delete physician request");
    }
  }
);

export const validateGroup = createAsyncThunk(
  "access/VALIDATE_GROUP",
  async (
    payload: { pin: string; tin: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.VALIDATE_GROUP, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      } else {
        dispatch(
          physicianGroupConfirmationLinks("physicianGroupConfirmationLinks")
        );
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to validate group");
    }
  }
);

export const validatePin = createAsyncThunk(
  "access/VALIDATE_PIN",
  async (payload: { pin: string }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(ACCESS_ENDPOINTS.VALIDATE_PIN, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to validate PIN");
    }
  }
);

export const deleteGroupAccess = createAsyncThunk(
  "access/DELETE_GROUP_ACCESS",
  async (id: number, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await fetch(
        ACCESS_ENDPOINTS.DELETE_GROUP_ACCESS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to delete group access");
    }
  }
);

export const checkRegistration = createAsyncThunk(
  "access/CHECK_REGISTRATION",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const queryString = buildQueryString(query);

      const response = await fetch(
        `${ACCESS_ENDPOINTS.CHECK_REGISTRATION}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }
      const result = await response.json();
      return result;
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to check registration");
    }
  }
);
export const searchPixeUsersByName = createAsyncThunk(
  "access/SEARCH_PIXE_USERS_BY_NAME",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      // Fix sorting parameters if present
      const fixedQuery = { ...query };

      if (fixedQuery.sort && Array.isArray(fixedQuery.sort)) {
        // Convert array of objects to array of strings like "lastName,asc"
        fixedQuery.sort = fixedQuery.sort.map((sortObj) => {
          if (typeof sortObj === "object" && sortObj !== null) {
            const field = Object.keys(sortObj)[0];
            const direction = sortObj[field as keyof typeof sortObj];
            return `${field},${direction}`;
          }
          return sortObj;
        });
      }
      const queryString = buildQueryString(query);
      const response = await fetch(
        `${ACCESS_ENDPOINTS.SEARCH_BY_NAME}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }
      const result = await response.json();
      return result;
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch filtered physicians");
    }
  }
);

export const fetchMyFilteredPhysicians = createAsyncThunk(
  "access/FETCH_MY_FILTERED_PHYSICIANS",
  async (query: QueryParams, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const queryString = buildQueryString(query);

      const response = await fetch(
        `${ACCESS_ENDPOINTS.MY_PHYSICIANS}?${queryString}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message ||
          `HTTP error: ${response.status} ${response.statusText}`
        );
      }

      return await response.json();
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch filtered physicians");
    }
  }
);

export const fetchMyPhysicianByMpin = createAsyncThunk(
  "access/FETCH_MY_PHYSICIAN_BY_MPIN",
  async (mpin: number | string, { rejectWithValue, dispatch }) => {
    try {
      dispatch(clearError());
      const response = await axios.get(
        `${ACCESS_ENDPOINTS.MY_PHYSICIANS}?mpin=${mpin}`
      );
      const data =
        response?.data?.data && response?.data?.data?.length > 0
          ? response.data.data[0]
          : null;

      return data
        ? {
          isRegistered: true,
          accessLevel:
            data.status === "pending"
              ? ACCESS_LEVELS.REQUESTED_ACCESS
              : ACCESS_LEVELS.NO_ACCESS,
        }
        : {};
    } catch (error) {
      handleApiError(error as any, "access", dispatch);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch physician by MPIN");
    }
  }
);

// Define types based on the actual Redux state structure
interface Physician {
  overrideIndicator: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  premiumSpecialty: string;
  geographicArea: string;
  active: boolean;
  eligible: boolean;
  medica: boolean;
  version: number;
  state: string;
  designation: string;
  published: string;
  registered: string | null;
  costEfficiencyRating: string;
  costEfficiencyScore: string;
  totalCostofCareScore: string;
  qltyScore: string;
  id: number;
  status: string;
  tins: number[];
  npis: number[];
  mpin: number;
  fullName: string;
}

// Update the Delegate interface to match the flat structure you're receiving
interface Delegate {
  id: number;
  mpin: number;
  status: string;
  fullName?: string;
  "user.email"?: string;
  "user.firstName"?: string;
  "user.lastName"?: string;
  "user.id"?: number;
  "user.username"?: string;
  // Optional nested user object for flexibility
  user?: {
    id?: number;
    mpin?: number | null;
    phone?: string | null;
    username?: string;
    firstName?: string;
    middleName?: string | null;
    lastName?: string;
    email?: string;
    emailVerified?: boolean;
    // ...other user properties
  };
}

interface DesignationPhase {
  phaseId: number;
  phaseName: string;
  version: number;
}

interface Group {
  userUsername: string | null;
  geographicArea: string;
  expired: string | null;
  userId: number;
  publicDisplayInd: string;
  active: boolean | null;
  MPIN: string | null;
  name: string;
  state: string;
  pending: string | null;
  TIN: string;
  groupID: number;
  id: number;
}

interface PixeUserRelationships {
  groupsAdministered: number[];
  physiciansAdministered: number[];
  physiciansDelegated: number[];
}

interface AccessUser {
  middleName: string | null;
  lastName: string;
  gender: string | null;
  dashboardHideImAPhysician: boolean;
  emailVerified: boolean;
  optumUUID: string;
  mpin: number | null;
  pixeUserRelationships: PixeUserRelationships;
  dashboardHideGroupAdminAccess: boolean;
  birthdate: string | null;
  nickname: string;
  phone: string | null;
  sso: string;
  physiciansAdministered: Record<string, boolean> | null;
  status: string;
  username: string;
  optumName: string;
  physiciansDelegated: Record<string, boolean> | null;
  dashboardHidePhysicianAccess: boolean;
  type: string;
  id: number | null;
  firstName: string;
  groupsAdministered: Record<string, boolean> | null;
  email: string;
  dashboardHideDelegateAccess: boolean;
}

interface PhysicianBeingAccessed {
  mpin: number | null;
  id: number | null;
  isRegistered: boolean;
  accessLevel: string;
}

interface PaginatedResponse<T> {
  data: T[];
  totalItems: number;
  currentPage: number;
  previousQuery: Record<string, any>;
}

// Correctly typed state interface based on actual Redux state
export interface AccessState {
  delegates: Delegate[];
  registrationResults: any[];
  approvedPhysicians: PaginatedResponse<Physician>;
  approvedAllPhysicians: PaginatedResponse<Physician>;
  pendingPhysicians: PaginatedResponse<Physician>;
  pendingAllPhysicians: PaginatedResponse<Physician>;
  physicianSearchResults: PaginatedResponse<Physician>;
  designationPhase: DesignationPhase;
  refreshUser: boolean;
  user: AccessUser;
  myFilteredPhysicians: PaginatedResponse<Physician>;
  allDelegates: PaginatedResponse<Delegate>;
  validatedGroup: any | null;
  groups: PaginatedResponse<Group>;
  approvedDelegates: PaginatedResponse<Delegate>;
  pendingDelegates: PaginatedResponse<Delegate>;
  physicianBeingAccessed: PhysicianBeingAccessed;
  pixeUserSearchResults: PaginatedResponse<any>;
  // TODO: Remove 'loading' property once migration to 'pendingPhysiciansLoading' and 'approvedPhysiciansLoading' is complete.
  loading: boolean;
  // new introduced loading variable
  pendingPhysiciansLoading: boolean;
  approvedPhysiciansLoading: boolean;
  error: string | null;
  // Add any other properties that are part of your state
  validatedPhysician: any | null;
  alert: {
    show: boolean;
    message: string;
    type: string;
    success: boolean;
  };
}

// Initialize with proper structure matching old app
const initialState: AccessState = {
  delegates: [],
  registrationResults: [],
  approvedPhysicians: {
    data: [],
    totalItems: 0,
    currentPage: 0,
    previousQuery: {},
  },
  approvedAllPhysicians: {
    data: [],
    totalItems: 0,
    currentPage: 0,
    previousQuery: {},
  },
  pendingPhysicians: {
    totalItems: 0,
    currentPage: 0,
    data: [],
    previousQuery: {},
  },
  pendingAllPhysicians: {
    totalItems: 0,
    currentPage: 0,
    data: [],
    previousQuery: {},
  },
  physicianSearchResults: {
    totalItems: 0,
    currentPage: 0,
    data: [],
    previousQuery: {},
  },
  designationPhase: {
    phaseId: 0,
    phaseName: "",
    version: 0,
  },
  refreshUser: false,
  user: {
    middleName: null,
    lastName: "",
    gender: null,
    dashboardHideImAPhysician: false,
    emailVerified: false,
    optumUUID: "",
    mpin: null,
    pixeUserRelationships: {
      groupsAdministered: [],
      physiciansAdministered: [],
      physiciansDelegated: [],
    },
    dashboardHideGroupAdminAccess: false,
    birthdate: null,
    nickname: "",
    phone: null,
    sso: "",
    physiciansAdministered: null,
    status: "",
    username: "",
    optumName: "",
    physiciansDelegated: null,
    dashboardHidePhysicianAccess: false,
    type: "",
    id: null,
    firstName: "",
    groupsAdministered: null,
    email: "",
    dashboardHideDelegateAccess: false,
  },
  myFilteredPhysicians: {
    totalItems: 0,
    currentPage: 0,
    data: null,
    previousQuery: {},
  },
  allDelegates: {
    data: [],
    totalItems: 0,
    currentPage: 0,
    previousQuery: {},
  },
  validatedGroup: null,
  validatedPhysician: null,
  groups: {
    data: [],
    totalItems: 0,
    currentPage: 0,
    previousQuery: {},
  },
  approvedDelegates: {
    totalItems: 0,
    currentPage: 0,
    data: [],
    previousQuery: {},
  },
  pendingDelegates: {
    totalItems: 0,
    currentPage: 0,
    data: [],
    previousQuery: {},
  },
  physicianBeingAccessed: {
    mpin: null,
    id: null,
    isRegistered: false,
    accessLevel: "Request Details", // Default value from Redux state
  },
  pixeUserSearchResults: {
    totalItems: 0,
    currentPage: 0,
    data: null,
    previousQuery: {},
  },
  alert: {
    show: false,
    message: "",
    type: "",
    success: false,
  },
  loading: false,
  pendingPhysiciansLoading: false,
  approvedPhysiciansLoading: false,
  error: null,
};

const accessSlice = createSlice({
  name: "access",
  initialState,
  reducers: {
    setPhysicianBeingAccessed: (
      state,
      action: PayloadAction<PhysicianBeingAccessed>
    ) => {
      state.physicianBeingAccessed = action.payload;
    },
    clearPhysicianSearchResults: (state) => {
      state.physicianSearchResults = {
        totalItems: 0,
        currentPage: 0,
        data: [],
        previousQuery: {},
      };
    },
    clearPixeUsers: (state) => {
      state.pixeUserSearchResults = {
        totalItems: 0,
        currentPage: 0,
        data: [],
        previousQuery: {},
      };
    },
    clearValidatedGroup: (state) => {
      state.validatedGroup = null;
    },
    resetError: (state) => {
      state.error = null;
    },
    clearAlert: (state) => {
      state.alert = {
        show: false,
        message: "",
        type: "",
        success: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // validateUser
      .addCase(validateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        validateUser.fulfilled,
        (state, action: PayloadAction<AccessUser>) => {
          state.loading = false;
          state.user = action.payload;
          state.refreshUser = false;
        }
      )
      .addCase(validateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Validation failed";
      })

      // updateAccessUser
      .addCase(
        updateAccessUser.fulfilled,
        (state, action: PayloadAction<AccessUser>) => {
          state.user = action.payload;
        }
      )
      // denyDelegate
      .addCase(denyDelegate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(denyDelegate.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshUser = true;
        state.approvedDelegates.data.push(action.payload);
        state.approvedDelegates.totalItems += 1;
      })
      .addCase(denyDelegate.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to deny delegate";
      })
      //approveDelegate
      // addDelegate
      .addCase(approveDelegate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveDelegate.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshUser = true;
        state.approvedDelegates.data.push(action.payload);
        state.approvedDelegates.totalItems += 1;
      })
      .addCase(approveDelegate.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to add delegate";
      })
      // approveDelegate
      .addCase(addDelegate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDelegate.fulfilled, (state, action) => {
        state.loading = false;
        state.refreshUser = true;
        state.approvedDelegates.data.push(action.payload);
        state.approvedDelegates.totalItems += 1;
      })
      .addCase(addDelegate.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to add delegate";
      })

      // deleteDelegate
      .addCase(deleteDelegate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDelegate.fulfilled, (state) => {
        state.loading = false;
        state.refreshUser = true;
      })
      .addCase(deleteDelegate.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to delete delegate";
      })

      // fetchMyPhysicians
      .addCase(fetchMyPhysicians.pending, (state, action) => {
        // will be remove later
        state.loading = true;
        state.pendingPhysiciansLoading = action.meta.arg.status === "pending" ? true : state.pendingPhysiciansLoading;
        state.approvedPhysiciansLoading = action.meta.arg.status === "approved" ? true : state.approvedPhysiciansLoading;
        state.error = null;
      })
      .addCase(fetchMyPhysicians.fulfilled, (state, action) => {
        state.loading = false;
        const queryStatus = action.meta.arg.status;
        const status = queryStatus === "approved" ? "approved" : "pending";

        if (status === "approved") {
          state.approvedPhysicians = action.payload;
          state.approvedPhysiciansLoading = false;
        } else {
          state.pendingPhysicians = action.payload;
          state.pendingPhysiciansLoading = false;
        }
      })
      .addCase(fetchMyPhysicians.rejected, (state, action) => {
        state.loading = false;
        state.pendingPhysiciansLoading = false;
        state.approvedPhysiciansLoading = false;
        state.error =
          (action.payload as string) || "Failed to fetch physicians";
      })
      // fetchAllMyPhysicians
      .addCase(fetchAllMyPhysicians.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllMyPhysicians.fulfilled, (state, action) => {
        state.loading = false;
        const queryStatus = action.meta.arg.status;
        const status = queryStatus === "approved" ? "approved" : "pending";

        if (status === "approved") {
          state.approvedAllPhysicians = action.payload;
        } else {
          state.pendingAllPhysicians = action.payload;
        }
      })

      .addCase(fetchAllMyPhysicians.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch All physicians";
      })
      // fetchDesignationPhase
      .addCase(fetchDesignationPhase.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignationPhase.fulfilled, (state, action) => {
        state.loading = false;
        state.designationPhase = action.payload;
      })
      .addCase(fetchDesignationPhase.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch designation phase";
      })

      // fetchMyGroups
      .addCase(fetchMyGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(fetchMyGroups.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload.responseData as string) || "Failed to fetch groups";
      })

      // searchPixeUsers
      .addCase(searchPixeUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPixeUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.pixeUserSearchResults = action.payload;
      })
      .addCase(searchPixeUsers.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to search PIXE users";
      })

      // fetchPixeUserByMpin
      .addCase(fetchPixeUserByMpin.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.physicianBeingAccessed = initialState.physicianBeingAccessed;
      })
      .addCase(fetchPixeUserByMpin.fulfilled, (state, action) => {
        state.loading = false;
        state.physicianBeingAccessed = action.payload;
      })
      .addCase(fetchPixeUserByMpin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch PIXE user by MPIN";
      })

      // searchPhysicians
      .addCase(searchPhysicians.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPhysicians.fulfilled, (state, action) => {
        state.loading = false;
        state.physicianSearchResults = action.payload;
      })
      .addCase(searchPhysicians.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to search physicians";
      })

      // requestPhysician
      .addCase(requestPhysician.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(requestPhysician.fulfilled, (state) => {
        state.loading = false;
        state.refreshUser = true;
      })
      .addCase(requestPhysician.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to request physician";
      })

      // deletePhysicianRequest
      .addCase(deletePhysicianRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePhysicianRequest.fulfilled, (state) => {
        state.loading = false;
        state.refreshUser = true;
        state.physicianBeingAccessed = initialState.physicianBeingAccessed;
      })
      .addCase(deletePhysicianRequest.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to delete physician request";
      })

      // validateGroup
      .addCase(validateGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validateGroup.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        state.alert = {
          show: true,
          message:
            "Success! You now have administrator access for the TIN below.",
          type: "success",
          success: true,
        };
        if (data && data.status && data.status !== 200) return;

        state.validatedGroup = data;
        // Update the newly added  group in the groups
        state.groups.data.push(data);
        state.groups.totalItems += 1;
        state.refreshUser = true;
      })
      .addCase(validateGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to validate group";
        state.alert = {
          show: true,
          message:
            "The PIN is case sensitive, ensure you enter the PIN as it appears in your letter.",
          type: "danger",
          success: false,
        };
      })

      // validatePin
      .addCase(validatePin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(validatePin.fulfilled, (state, action) => {
        state.loading = false;
        const { user } = state;
        if (action.payload.data && action.payload.data.mpin) {
          user.mpin = action.payload.data.mpin;
        }
      })
      .addCase(validatePin.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to validate PIN";
      })

      // deleteGroupAccess
      .addCase(deleteGroupAccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGroupAccess.fulfilled, (state) => {
        state.loading = false;
        state.refreshUser = true;
      })
      .addCase(deleteGroupAccess.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to delete group access";
      })

      // checkRegistration
      .addCase(checkRegistration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkRegistration.fulfilled, (state, action) => {
        state.loading = false;
        state.physicianSearchResults = action.payload;
      })
      .addCase(checkRegistration.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to check registration";
      })

      // fetchMyFilteredPhysicians
      .addCase(fetchMyFilteredPhysicians.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyFilteredPhysicians.fulfilled, (state, action) => {
        state.loading = false;
        state.myFilteredPhysicians = action.payload;
      })
      .addCase(fetchMyFilteredPhysicians.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch filtered physicians";
      })

      // fetchMyActiveDelegates
      .addCase(fetchMyActiveDelegates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyActiveDelegates.fulfilled, (state, action) => {
        state.loading = false;
        state.allDelegates = action.payload;
      })
      .addCase(fetchMyActiveDelegates.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch active delegates";
      })
      // fetchMyDelegates
      .addCase(fetchMyDelegates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyDelegates.fulfilled, (state, action) => {
        state.loading = false;
        const queryStatus = action.meta.arg.status;
        const status = queryStatus === "approved" ? "approved" : "pending";
        if (status === "approved") {
          state.approvedDelegates = action.payload;
        } else {
          state.pendingDelegates = action.payload;
        }
      })
      .addCase(fetchMyDelegates.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch active delegates";
      })

      // fetchMyPhysicianByMpin
      .addCase(fetchMyPhysicianByMpin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMyPhysicianByMpin.fulfilled, (state, action) => {
        state.loading = false;
        state.physicianBeingAccessed = {
          ...state.physicianBeingAccessed,
          ...action.payload,
        };
      })
      .addCase(fetchMyPhysicianByMpin.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch physician by MPIN";
      })
      // searchPixeUsersByName
      .addCase(searchPixeUsersByName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchPixeUsersByName.fulfilled, (state, action) => {
        state.loading = false;
        state.pixeUserSearchResults = action.payload;
      })
      .addCase(searchPixeUsersByName.rejected, (state, action) => {
        state.loading = false;
        state.error =
          (action.payload as string) || "Failed to fetch filtered physicians";
      })
      .addCase(updateUserPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.user.user = action.payload;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to update user preferences";
      })
      .addCase(registerAsPhysician.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerAsPhysician.fulfilled, (state, action) => {
        state.loading = false;
        const physicianData = action.payload.data ?? action.payload;
        state.validatedPhysician = physicianData;
        if (physicianData?.mpin) {
          state.user = { ...state.user, mpin: physicianData.mpin };
        }
      })
      .addCase(registerAsPhysician.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to register as physician";
      });
  },
});

export const {
  setPhysicianBeingAccessed,
  clearPhysicianSearchResults,
  clearPixeUsers,
  clearValidatedGroup,
  resetError,
  clearAlert,
} = accessSlice.actions;

export default accessSlice.reducer;
